import { useEffect, useState, useMemo, useCallback } from 'react'
import type { ReactNode } from 'react'
import {
  Box,
  Typography,
  Grid,
  Skeleton,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import GetAppIcon from '@mui/icons-material/GetApp'
import { useTheme } from '@/contexts/ThemeContext'
import { useNotification } from '@/hooks/useNotification'
import { useChainId } from 'wagmi'
import { getEnv } from '@/config/env'
import { formatTokenBalance } from '@/utils/formatting'
import {
  DEFAULT_TOKEN_DECIMALS,
  POLLING_INTERVAL_STATISTICS,
} from '@/constants/main'
import {
  createCardStyles,
  createSecondaryTextStyles,
} from '@/components/shared/commonStyles'
import SectionHeader from '@/components/shared/SectionHeader'
import { SKELETON_WIDTH_MEDIUM } from '@/constants/main'

interface StatisticsData {
  tokensBurned: string
  tokensMinted: string
  tokensTransferred: string
  txVolume: string
  burnsCount: number
  mintsCount: number
  transfersCount: number
  txCount: number
  averageTxSize: string
}

export default function StatisticsBlock() {
  const { colors } = useTheme()
  const { showSuccess, showError } = useNotification()
  const chainId = useChainId()
  const [statistics, setStatistics] = useState<StatisticsData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const { apiUrl, adminBearerToken } = useMemo(() => getEnv(), [])

  const fetchStatistics = useCallback(async () => {
    if (!apiUrl) {
      setError('API URL is not configured')
      setIsLoading(false)
      return
    }

    if (!chainId) {
      setError('Chain ID is not available')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const response = await fetch(`${apiUrl}/v1/admin/statistics/${chainId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminBearerToken}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch statistics: ${response.statusText}`)
      }

      const data: StatisticsData = await response.json()
      setStatistics(data)
    } catch (err) {
      console.error('Failed to fetch statistics:', err)
      setError(
        err instanceof Error ? err.message : 'Failed to fetch statistics'
      )
    } finally {
      setIsLoading(false)
    }
  }, [apiUrl, adminBearerToken, chainId])

  useEffect(() => {
    fetchStatistics()

    const interval = setInterval(fetchStatistics, POLLING_INTERVAL_STATISTICS)

    return () => {
      clearInterval(interval)
    }
  }, [fetchStatistics])

  const formatWeiValue = (weiValue: string): string => {
    try {
      const bigintValue = BigInt(weiValue)
      return formatTokenBalance(bigintValue, DEFAULT_TOKEN_DECIMALS)
    } catch {
      return '0'
    }
  }

  const convertStatisticsToCSV = useCallback((): string => {
    if (!statistics) {
      return ''
    }

    const headers = ['Metric', 'Value']

    const rows = [
      ['Tokens Minted', formatWeiValue(statistics.tokensMinted)],
      ['Tokens Burned', formatWeiValue(statistics.tokensBurned)],
      ['Tokens Transferred', formatWeiValue(statistics.tokensTransferred)],
      ['Transaction Volume', formatWeiValue(statistics.txVolume)],
      ['Mints Count', statistics.mintsCount.toString()],
      ['Burns Count', statistics.burnsCount.toString()],
      ['Transfers Count', statistics.transfersCount.toString()],
      ['Total Transactions', statistics.txCount.toString()],
      ['Average Transaction Size', formatWeiValue(statistics.averageTxSize)],
    ]

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n')

    return csvContent
  }, [statistics])

  const handleExportStatistics = useCallback(() => {
    if (!statistics) {
      showError('No statistics data available to export')
      return
    }

    try {
      const csvContent = convertStatisticsToCSV()
      // Add UTF-8 BOM for proper encoding on mobile devices
      const BOM = '\uFEFF'
      const csvWithBOM = BOM + csvContent

      const today = new Date().toISOString().split('T')[0]
      const fileName = `statistics_${today}.csv`

      const isMetaMaskWebView =
        navigator.userAgent.includes('MetaMaskMobile') ||
        navigator.userAgent.includes('Mobi') ||
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

      if (isMetaMaskWebView) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(csvContent).then(() => {
            showSuccess('CSV content copied to clipboard!')
          })
        } else {
          showError('Clipboard not available. Please use desktop browser.')
        }
      } else {
        const blob = new Blob([csvWithBOM], {
          type: 'text/csv',
        })

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()

        setTimeout(() => {
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        }, 100)

        showSuccess('Statistics exported successfully')
      }
    } catch (error) {
      console.error('Failed to export statistics:', error)
      showError('Failed to export statistics')
    }
  }, [statistics, convertStatisticsToCSV, showSuccess, showError])

  const StatItem = ({
    label,
    value,
    isLoading: itemLoading,
  }: {
    label: string
    value: string | number
    isLoading: boolean
  }) => (
    <Box>
      <Typography
        variant="body2"
        sx={{ ...createSecondaryTextStyles(colors), mb: 0.5 }}
      >
        {label}
      </Typography>
      {itemLoading ? (
        <Skeleton
          variant="text"
          width={SKELETON_WIDTH_MEDIUM}
          sx={{ bgcolor: `${colors.primary}33` }}
        />
      ) : (
        <Typography
          variant="body1"
          sx={{ color: colors.text, fontWeight: 500 }}
        >
          {value}
        </Typography>
      )}
    </Box>
  )

  const StatisticsDivider = () => (
    <Grid item xs={12}>
      <Divider
        sx={{
          borderColor: colors.primary,
          opacity: 0.3,
          mt: 2,
          mb: 1,
        }}
      />
    </Grid>
  )

  const StatisticsGridItem = ({ children }: { children: ReactNode }) => (
    <Grid item xs={12} sm={6} md={4}>
      {children}
    </Grid>
  )

  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeader
        title="Statistics"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Export Statistics to CSV">
              <span>
                <IconButton
                  onClick={handleExportStatistics}
                  disabled={!statistics || isLoading}
                  sx={{
                    color: colors.primary,
                    '&:hover': {
                      backgroundColor: `${colors.primary}22`,
                    },
                    '&:disabled': {
                      color: colors.text,
                      opacity: 0.5,
                    },
                  }}
                >
                  <GetAppIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh Statistics">
              <span>
                <IconButton
                  onClick={fetchStatistics}
                  disabled={isLoading}
                  sx={{
                    color: colors.primary,
                    '&:hover': {
                      backgroundColor: `${colors.primary}22`,
                    },
                    '&:disabled': {
                      color: colors.text,
                      opacity: 0.5,
                    },
                  }}
                >
                  <RefreshIcon
                    sx={{
                      animation: isLoading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(0deg)',
                        },
                        '100%': {
                          transform: 'rotate(360deg)',
                        },
                      },
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      />
      <Box sx={createCardStyles(colors)}>
        {error ? (
          <Typography
            variant="body2"
            sx={{ color: colors.error, textAlign: 'center' }}
          >
            {error}
          </Typography>
        ) : (
          <Grid container spacing={3}>
            <StatisticsGridItem>
              <StatItem
                label="Tokens Minted"
                value={
                  statistics ? formatWeiValue(statistics.tokensMinted) : '—'
                }
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsGridItem>
              <StatItem
                label="Tokens Burned"
                value={
                  statistics ? formatWeiValue(statistics.tokensBurned) : '—'
                }
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsGridItem>
              <StatItem
                label="Tokens Transferred"
                value={
                  statistics
                    ? formatWeiValue(statistics.tokensTransferred)
                    : '—'
                }
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsDivider />
            <StatisticsGridItem>
              <StatItem
                label="Mints Count"
                value={statistics ? statistics.mintsCount : '—'}
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsGridItem>
              <StatItem
                label="Burns Count"
                value={statistics ? statistics.burnsCount : '—'}
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsGridItem>
              <StatItem
                label="Transfers Count"
                value={statistics ? statistics.transfersCount : '—'}
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsDivider />
            <StatisticsGridItem>
              <StatItem
                label="Transaction Volume"
                value={statistics ? formatWeiValue(statistics.txVolume) : '—'}
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsGridItem>
              <StatItem
                label="Total Transactions"
                value={statistics ? statistics.txCount : '—'}
                isLoading={isLoading}
              />
            </StatisticsGridItem>
            <StatisticsGridItem>
              <StatItem
                label="Average Transaction Size"
                value={
                  statistics ? formatWeiValue(statistics.averageTxSize) : '—'
                }
                isLoading={isLoading}
              />
            </StatisticsGridItem>
          </Grid>
        )}
      </Box>
    </Box>
  )
}
